// Validation utilities for React Firebase Registration System

import { VALIDATION_RULES, FILE_UPLOAD, ERROR_MESSAGES } from "./constants.js";

export const validateEmail = (email) => {
  if (!email || typeof email !== "string") return false;
  return VALIDATION_RULES.EMAIL_REGEX.test(email.trim());
};

export const validateRequired = (value) => {
  return value != null && value.toString().trim().length > 0;
};

export const validateMobileNumber = (mobile) => {
  if (!mobile || typeof mobile !== "string") return false;
  // International mobile number format: +[country code][number] (E.164 format)
  return VALIDATION_RULES.MOBILE_REGEX.test(mobile.trim());
};

export const validatePassportNumber = (passport) => {
  if (!passport || typeof passport !== "string") return false;
  // Alphanumeric, 6-9 characters
  return VALIDATION_RULES.PASSPORT_REGEX.test(passport.trim().toUpperCase());
};

export const validateNationalId = (nationalId) => {
  if (!nationalId || typeof nationalId !== "string") return false;
  // ID Number: 4-15 alphanumeric characters
  return VALIDATION_RULES.NATIONAL_ID_REGEX.test(nationalId.trim());
};

export const validateFileSize = (file, maxSizeMB = FILE_UPLOAD.MAX_SIZE_MB) => {
  if (!file || typeof file.size !== "number") return false;
  const maxSizeBytes = maxSizeMB * 1024 * 1024;
  return file.size <= maxSizeBytes;
};

export const validateFileType = (file) => {
  if (!file || typeof file.type !== "string") return false;
  return FILE_UPLOAD.ALLOWED_TYPES.includes(file.type);
};

// Additional validation functions for enhanced functionality

export const validatePassword = (password) => {
  if (!password || typeof password !== "string") return false;

  const minLength = password.length >= VALIDATION_RULES.PASSWORD_MIN_LENGTH;
  const hasUppercase = /[A-Z]/.test(password);
  const hasLowercase = /[a-z]/.test(password);
  const hasNumbers = /\d/.test(password);
  const hasSymbols = /[!@#$%^&*(),.?":{}|<>]/.test(password);

  return minLength && hasUppercase && hasLowercase && hasNumbers && hasSymbols;
};

export const validatePasswordStrength = (password) => {
  if (!password) return { score: 0, feedback: [] };

  const feedback = [];
  let score = 0;

  if (password.length >= 8) score += 1;
  else feedback.push("Password must be at least 8 characters long");

  if (/[A-Z]/.test(password)) score += 1;
  else feedback.push("Password must contain uppercase letters");

  if (/[a-z]/.test(password)) score += 1;
  else feedback.push("Password must contain lowercase letters");

  if (/\d/.test(password)) score += 1;
  else feedback.push("Password must contain numbers");

  if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) score += 1;
  else feedback.push("Password must contain special characters");

  return { score, feedback };
};

export const validateName = (name) => {
  if (!name || typeof name !== "string") return false;
  const trimmedName = name.trim();
  // Name should be at least 2 characters and contain only letters, spaces, hyphens, and apostrophes
  return trimmedName.length >= 2 && /^[a-zA-Z\s\-']+$/.test(trimmedName);
};

export const validateCity = (city) => {
  if (!city || typeof city !== "string") return false;
  const trimmedCity = city.trim();
  // City should be at least 2 characters and contain only letters, spaces, and hyphens
  return trimmedCity.length >= 2 && /^[a-zA-Z\s\-]+$/.test(trimmedCity);
};

export const validateKebele = (kebele) => {
  if (!kebele || typeof kebele !== "string") return false;
  const trimmedKebele = kebele.trim();
  // Kebele can be numeric or alphanumeric, 1-3 characters
  return (
    trimmedKebele.length >= 1 &&
    trimmedKebele.length <= 3 &&
    /^[a-zA-Z0-9]+$/.test(trimmedKebele)
  );
};

// File validation with detailed error messages
export const validateFile = (file) => {
  const errors = [];

  if (!file) {
    errors.push("No file selected");
    return { isValid: false, errors };
  }

  if (!validateFileSize(file)) {
    errors.push(`File size must be less than ${FILE_UPLOAD.MAX_SIZE_MB}MB`);
  }

  if (!validateFileType(file)) {
    errors.push("File must be an image (JPEG, PNG, GIF) or PDF");
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

// Enhanced form validation with detailed error reporting
export const getValidationErrors = (formData) => {
  const errors = {};

  // User info validation
  if (!validateRequired(formData.userInfo?.fullName)) {
    errors.fullName = ERROR_MESSAGES.REQUIRED_FIELD;
  } else if (!validateName(formData.userInfo.fullName)) {
    errors.fullName =
      "Please enter a valid full name (letters, spaces, hyphens, and apostrophes only)";
  }

  if (!validateRequired(formData.userInfo?.country)) {
    errors.country = ERROR_MESSAGES.REQUIRED_FIELD;
  }

  if (!validateRequired(formData.userInfo?.cityId)) {
    errors.cityId = ERROR_MESSAGES.REQUIRED_FIELD;
  }

  if (!validateRequired(formData.userInfo?.email)) {
    errors.email = ERROR_MESSAGES.REQUIRED_FIELD;
  } else if (!validateEmail(formData.userInfo?.email)) {
    errors.email = ERROR_MESSAGES.INVALID_EMAIL;
  }

  if (!validateRequired(formData.userInfo?.mobileNumber)) {
    errors.mobileNumber = ERROR_MESSAGES.REQUIRED_FIELD;
  } else if (!validateMobileNumber(formData.userInfo?.mobileNumber)) {
    errors.mobileNumber = ERROR_MESSAGES.INVALID_MOBILE;
  }

  // Passport is optional - only validate if provided
  if (
    formData.userInfo?.passportNumber &&
    formData.userInfo.passportNumber.trim()
  ) {
    if (!validatePassportNumber(formData.userInfo.passportNumber)) {
      errors.passportNumber = ERROR_MESSAGES.INVALID_PASSPORT;
    }
  }

  if (!validateRequired(formData.userInfo?.nationalId)) {
    errors.nationalId = ERROR_MESSAGES.REQUIRED_FIELD;
  } else if (!validateNationalId(formData.userInfo?.nationalId)) {
    errors.nationalId = ERROR_MESSAGES.INVALID_NATIONAL_ID;
  }

  // Representative info validation (all optional - only validate format if provided)
  if (
    formData.representativeInfo?.fullName &&
    !validateName(formData.representativeInfo.fullName)
  ) {
    errors.representativeFullName =
      "Please enter a valid representative full name";
  }

  if (
    formData.representativeInfo?.nationalId &&
    !validateNationalId(formData.representativeInfo.nationalId)
  ) {
    errors.representativeNationalId = ERROR_MESSAGES.INVALID_NATIONAL_ID;
  }

  if (
    formData.representativeInfo?.kebele &&
    !validateKebele(formData.representativeInfo.kebele)
  ) {
    errors.representativeKebele =
      "Please enter a valid kebele (1-3 alphanumeric characters)";
  }

  // File validation
  if (!formData.paymentReceipt) {
    errors.paymentReceipt = ERROR_MESSAGES.REQUIRED_FIELD;
  } else {
    const fileValidation = validateFile(formData.paymentReceipt);
    if (!fileValidation.isValid) {
      errors.paymentReceipt = fileValidation.errors.join(", ");
    }
  }

  // Terms acceptance
  if (!formData.termsAccepted) {
    errors.termsAccepted = "You must accept the terms and conditions";
  }

  return errors;
};

// Utility function to check if form has any validation errors
export const hasValidationErrors = (errors) => {
  return Object.keys(errors).length > 0;
};

// Utility function to get first validation error message
export const getFirstError = (errors) => {
  const errorKeys = Object.keys(errors);
  return errorKeys.length > 0 ? errors[errorKeys[0]] : null;
};

// Utility function to validate individual field
export const validateField = (fieldName, value, formData = {}) => {
  const tempFormData = { ...formData };

  // Set the field value in the appropriate location
  if (fieldName.startsWith("representative")) {
    const actualFieldName = fieldName
      .replace("representative", "")
      .toLowerCase();
    tempFormData.representativeInfo = {
      ...tempFormData.representativeInfo,
      [actualFieldName]: value,
    };
  } else if (fieldName === "paymentReceipt") {
    tempFormData.paymentReceipt = value;
  } else if (fieldName === "termsAccepted") {
    tempFormData.termsAccepted = value;
  } else {
    // For regular user info fields
    tempFormData.userInfo = {
      ...tempFormData.userInfo,
      [fieldName]: value,
    };
  }

  const errors = getValidationErrors(tempFormData);

  // Map field names to error keys
  const errorKeyMap = {
    fullName: "fullName",
    country: "country",
    city: "city",
    email: "email",
    mobileNumber: "mobileNumber",
    passportNumber: "passportNumber",
    nationalId: "nationalId",
    representativeFullName: "representativeFullName",
    representativeNationalId: "representativeNationalId",
    representativeCity: "representativeCity",
    representativeDistrict: "representativeDistrict",
    representativeKebele: "representativeKebele",
    paymentReceipt: "paymentReceipt",
    termsAccepted: "termsAccepted",
  };

  const errorKey = errorKeyMap[fieldName] || fieldName;
  return errors[errorKey] || null;
};
